"use client";

import React from "react";
import { Textarea, Button, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-auth";

import { useTheme } from "@/hooks/use-theme";

interface PromptInputProps {
  value: string;
  onValueChange: (val: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onStopStreaming?: () => void;
  onPaste?: (e: React.ClipboardEvent) => void;
  streaming?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  messagesCount?: number;
  hasFilesUploading?: boolean;
  isReadOnly?: boolean;
}

export const PromptInputInvestAI: React.FC<PromptInputProps> = ({
  value,
  onValueChange,
  onSubmit,
  onStopStreaming,
  onPaste,
  streaming = false,
  isUploading = false,
  disabled = false,
  messagesCount = 0,
  hasFilesUploading = false,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { brand } = useBrand();
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      // Call form submission function
      onSubmit?.(event as unknown as React.FormEvent<HTMLInputElement>);
    }
  };
  const placeholder = messagesCount === 0 ? t("Start new chat") : t("Type a message");

  return (
    <div className="relative w-full px-4 my-3">
      <Textarea
        className={cn("focus:outline-none", isReadOnly && "cursor-not-allowed")}
        classNames={{
          inputWrapper: cn(
            "shadow-none pr-12",
            theme === "light"
              ? "hover:!bg-white bg-white focus-within:!bg-white"
              : "hover:!bg-brand-secondary !bg-brand-secondary focus-within:!bg-brand-secondary",
          ),
          input: cn(
            "pr-2 text-medium focus:outline-none",
            theme === "light"
              ? "[&::selection]:bg-brand-secondary [&::selection]:bg-brand-secondary [&::-moz-selection]:bg-brand-secondary [&::-moz-selection]:bg-brand-secondary"
              : "text-white",
            isReadOnly && "opacity-70 cursor-not-allowed",
          ),
        }}
        disabled={disabled}
        minRows={1}
        placeholder={placeholder}
        radius="lg"
        readOnly={isReadOnly}
        value={value}
        variant="flat"
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={handleKeyDown as any}
        onPaste={onPaste}
      />

      <Tooltip showArrow content={streaming ? t("Stop streaming") : t("Send message")}>
        <Button
          isIconOnly
          aria-label={streaming ? "Stop streaming" : "Send message"}
          className="!absolute right-6 top-1/2 -translate-y-1/2 z-10"
          color={
            streaming
              ? "danger"
              : !value || isUploading || hasFilesUploading
                ? "default"
                : undefined
          }
          disabled={streaming ? false : !value || isUploading || hasFilesUploading}
          radius="lg"
          size="sm"
          style={
            !streaming && value && !isUploading && !hasFilesUploading
              ? {
                  backgroundColor:
                    theme === "dark" ? brand.colors.dark.tertiary : brand.colors.light.tertiary,
                  color: "white",
                }
              : undefined
          }
          type={streaming ? "button" : "submit"}
          variant="solid"
          onPress={streaming ? onStopStreaming : undefined}
        >
          <Icon
            className={cn(
              "[&>path]:stroke-[2px]",
              streaming
                ? "text-danger-foreground"
                : !value || isUploading || hasFilesUploading
                  ? "text-default-600"
                  : "text-white",
            )}
            icon={streaming ? "solar:stop-circle-linear" : "solar:arrow-up-linear"}
            width={20}
          />
        </Button>
      </Tooltip>
    </div>
  );
};
